import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage from "@insite/mobius/LazyImage/LazyImage";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tSurya/theme/design";
import React from "react";
import { css } from "styled-components";

const enum fields {
    title = "title",
    image = "image",
    desc = "desc",
}

interface SlideModel {
    fields: {
        description: string;
    };
}
interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.image]: string;
        [fields.desc]: SlideModel[];
    };
}

type Props = OwnProps;

export interface SurTabularDataStyles {
    container?: GridContainerProps;
    imageStyles?: InjectableCss;
    infoContainer?: InjectableCss;
    descInnerContainer?: InjectableCss;
    imageContainer?: GridItemProps;
    title?: InjectableCss;
    rightGridContainer?: GridContainerProps;
    number?: InjectableCss;
    descriptionStyle?: InjectableCss;
    numberContainer?: InjectableCss;
    mainContainer?: InjectableCss;
}

export const surTabularDataStyles: SurTabularDataStyles = {
    rightGridContainer: {},
    container: {
        css: css`
            /* margin: 0 auto; */
            /* border: 1px solid red; */
            display: flex;
            justify-content: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        align-items: center;
                        flex-direction: column;
                    `,
                    css`
                        align-items: center;
                        flex-direction: column;
                    `,
                    css`
                        align-items: center;
                        flex-direction: column;
                    `,
                    null,
                    null,
                ])}

            > div[class^="GridOffset"] {
                margin: 0;
            }
        `,
    },
    title: {
        css: css`
            font-family: ${TYPOGRAPHY.fontFamily.title};
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            color: ${getColor("text.main")};
            font-size: ${TYPOGRAPHY.size.xxlarge};
            line-height: ${TYPOGRAPHY.lineHeight.larger};
            text-align: center;
            margin-bottom: 36px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-bottom: 16px;
                    `,
                    css`
                        margin-bottom: 16px;
                    `,
                    css`
                        margin-bottom: 16px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    imageStyles: {
        css: css`
            width: 100%;
            max-width: 462px;
            max-height: 500px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: flex;
                    `,
                    css`
                        display: flex;
                    `,
                    css`
                        display: flex;
                    `,
                    null,
                    null,
                ])}

            > img {
                max-width: 462px;
                max-height: 500px;
                height: 100%;
            }
        `,
    },
    numberContainer: {
        css: css`
            display: grid;
            grid-template-columns: 30px 1fr;
            flex-basis: 100%;
            max-width: 100%;
        `,
    },
    descInnerContainer: {
        css: css``,
    },
    imageContainer: {
        css: css`
            padding: 0;
            max-height: 500px;
        `,
    },
    descriptionStyle: {
        css: css`
            margin-left: 18px;
            font-family: "Mulish";
            font-style: normal;
            font-weight: 400;
            font-size: 14px;
            line-height: 22px;
            color: ${getColor("text.main")};
            max-width: 249px;
        `,
    },
    number: {
        css: css`
            display: flex;
            flex-direction: row;
            height: 30px;
            width: 30px;
            border: 1px solid ${getColor("common.border")};
            border-radius: 40px;
            background: #ffffff;
            display: flex;
            align-items: center;
            justify-content: center;
            font-family: "Mulish";
            font-style: normal;
            font-weight: 700;
            font-size: 14px;
            line-height: 22px;
            text-align: center;
            color: #545454;
        `,
    },
    infoContainer: {
        css: css`
            display: grid;
            grid-template-columns: 1fr 1fr;
            column-gap: 30px;
            flex-basis: 66.666%;
            max-width: 66.666%;
            padding-top: 55px;
            padding-bottom: 55px;
            padding-left: 45px;
            max-width: 704px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        grid-template-columns: 1fr;
                        padding: 20px;
                        row-gap: 10px;
                    `,
                    css`
                        grid-template-columns: 1fr;
                        padding: 20px;
                        row-gap: 10px;
                    `,
                    css`
                        grid-template-columns: 1fr;
                        padding: 0px;
                        padding-top: 20px;
                        row-gap: 10px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    mainContainer: {
        css: css`
            max-width: 1440px;
            margin: 0 auto;
            padding: 60px 0px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 30px 0px;
                    `,
                    css`
                        padding: 30px 0px;
                    `,
                    css`
                        padding: 30px 0px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
};

const styles = surTabularDataStyles;

const BenefitsAndPerks: React.FC<Props> = ({ fields }) => {
    return (
        <StyledWrapper {...styles.mainContainer}>
            <Typography as="h2" {...styles.title}>
                {fields.title}
            </Typography>

            <StyledWrapper {...styles.container}>
                <StyledWrapper {...styles.imageContainer}>
                    <LazyImage
                        src={fields.image}
                        {...styles.imageStyles}
                        altText={fields.title || "Benefits and Perks"}
                    />
                </StyledWrapper>
                <StyledWrapper {...styles.infoContainer}>
                    {fields.desc?.map((desc, index) => (
                        <>
                            <StyledWrapper {...styles.numberContainer}>
                                <StyledWrapper {...styles.number}>{index + 1}</StyledWrapper>
                                <StyledWrapper {...styles.descriptionStyle}>{desc.fields.description}</StyledWrapper>
                            </StyledWrapper>
                        </>
                    ))}
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const BenefitsAndPerksWidget: WidgetModule = {
    component: BenefitsAndPerks,
    definition: {
        group: "Common",
        displayName: "Benefits and Perks Widget",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.image,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                displayName: "Image",
                fieldType: "General",
            },
            {
                name: fields.desc,
                editorTemplate: "ListField",
                fieldType: "Translatable",
                getDisplay: ({ fields: { desc } }) => desc || "",
                defaultValue: [],
                fieldDefinitions: [
                    {
                        name: "description",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
            },
        ],
    },
};

export default BenefitsAndPerksWidget;
